// The search module invokes neighborhood's query function,
// formats the results as story items, and then opens a
// page to present them.

const pageHandler = require('./pageHandler')
const random = require('./random')
const link = require('./link')
const { newPage } = require('./page')
const resolve = require('./resolve')
let page = require('./page')
const { isMobileViewport } = require('./viewport')

const deepCopy = object => JSON.parse(JSON.stringify(object))

// Visual viewport inset that usually means the mobile IME is open (popstate back handling).
const KEYBOARD_INSET_PX = 80

// --- Mobile search history (Android back: IME first, then dismiss overlay) ---
// First back often dismisses the IME without popstate; the next popstate hides
// the results overlay and leaves the query so a later tap can reopen search.
let searchHistoryPushed = false
let ignoringSearchPopstate = false
let hideSearchOverlayKeepQuery = () => {
  document.querySelector('.incremental-search')?.remove()
}

const searchImeOpen = () => {
  const vv = window.visualViewport
  if (!vv || typeof vv.height !== 'number') return false
  // Do not latch keyboard state here — after native IME dismiss without popstate
  // a stale latch would steal the second hardware-back press.
  return window.innerHeight - (vv.height + (vv.offsetTop || 0)) > KEYBOARD_INSET_PX
}

const pushSearchHistory = () => {
  if (searchHistoryPushed || !isMobileViewport()) return
  try {
    history.pushState(Object.assign({}, history.state, { wikiMobileSearch: 1 }), '')
    searchHistoryPushed = true
  } catch (_) {
    /* private mode / missing history */
  }
}

const consumeSearchHistory = () => {
  if (!searchHistoryPushed) return
  searchHistoryPushed = false
  if (!history.state?.wikiMobileSearch) return
  ignoringSearchPopstate = true
  history.back()
  requestAnimationFrame(() => {
    ignoringSearchPopstate = false
  })
}

window.addEventListener(
  'popstate',
  e => {
    if (ignoringSearchPopstate) {
      e.stopImmediatePropagation()
      return
    }
    if (!searchHistoryPushed) return
    const searchUiUp =
      document.body.classList.contains('wiki-mobile-searching') ||
      !!document.querySelector('.incremental-search')
    if (!searchUiUp) {
      // Overlay already gone (clear / result); don't steal lineup navigation.
      searchHistoryPushed = false
      return
    }
    e.stopImmediatePropagation()
    const input = document.querySelector('input.search')
    if (searchImeOpen() && input && document.activeElement === input) {
      input.blur()
      try {
        history.pushState(Object.assign({}, history.state, { wikiMobileSearch: 1 }), '')
      } catch (_) {
        /* private mode / missing history */
      }
      return
    }
    searchHistoryPushed = false
    hideSearchOverlayKeepQuery()
  },
  true,
)

// From reference.coffee
const emit = function ($item, item) {
  let slug = item.slug
  if (item.title) {
    slug ||= page.asSlug(item.title)
  }
  slug ||= 'welcome-visitors'
  const site = item.site
  resolve.resolveFrom(site, () =>
    $item.append(`\
<p>
<img class='remote'
  src='${wiki.site(site).flag()}'
  title='${site}'
  data-site="${site}"
  data-slug="${slug}"
>
${resolve.resolveLinks(`[[${item.title || slug}]]`)}
—
${resolve.resolveLinks(item.text)}
</p>\
`),
  )
}
const finishClick = function (e, name) {
  e.preventDefault()
  if (!e.shiftKey) {
    page = $(e.target).parents('.page')
  }
  link.doInternalLink(name, page, $(e.target).data('site'))
  return false
}

const createSearch = function ({ neighborhood }) {
  let viewportBound = false
  let bgScrollBlocked = false
  let restoringSearchFocus = false
  let layoutRaf = null

  const searchInput = () => document.querySelector('input.search')

  const clearRestoringSearchFocus = () => {
    restoringSearchFocus = false
  }

  const isRestoringSearchFocus = () => restoringSearchFocus

  const clearOverlayPin = overlay => {
    if (!overlay) return
    overlay.style.position = ''
    overlay.style.left = ''
    overlay.style.right = ''
    overlay.style.width = ''
    overlay.style.maxWidth = ''
    overlay.style.bottom = ''
    overlay.style.top = ''
    overlay.style.height = ''
    overlay.style.maxHeight = ''
    overlay.style.zIndex = ''
    overlay.style.boxSizing = ''
  }

  // --- Mobile results overlay (fixed panel above footer; footer stays on resizes-content) ---
  const blockBackgroundScroll = e => {
    // Stop lineup under the results from scrolling without overflow:hidden on
    // body/main (that dismisses the IME when applied on focus).
    if (!document.body.classList.contains('wiki-mobile-searching')) return
    if (e.target.closest?.('.incremental-search, footer, input.search')) return
    e.preventDefault()
  }

  const setBackgroundScrollBlocked = on => {
    if (on && !bgScrollBlocked) {
      document.addEventListener('touchmove', blockBackgroundScroll, { passive: false })
      bgScrollBlocked = true
    } else if (!on && bgScrollBlocked) {
      document.removeEventListener('touchmove', blockBackgroundScroll)
      bgScrollBlocked = false
    }
  }

  const pinOverlayAboveFooter = (overlay, footer) => {
    if (!overlay || !footer) return
    const fr = footer.getBoundingClientRect()
    const vv = window.visualViewport
    // Sit on top of the (one-row) footer — while searching, CSS collapses the
    // flag band so fr.top is the standard controls-row shelf, not a tall stack.
    const coverBottom = fr.top
    const top = Math.max(0, vv ? Math.round(vv.offsetTop) : 0)
    const bottom = Math.max(0, Math.round(window.innerHeight - coverBottom))
    overlay.style.position = 'fixed'
    overlay.style.left = '0'
    overlay.style.right = '0'
    overlay.style.width = '100%'
    overlay.style.maxWidth = 'none'
    overlay.style.top = `${top}px`
    overlay.style.bottom = `${bottom}px`
    overlay.style.height = 'auto'
    overlay.style.maxHeight = 'none'
    overlay.style.zIndex = '1100'
    overlay.style.boxSizing = 'border-box'
    overlay.style.overflowY = 'auto'
  }

  const layoutMobileSearch = () => {
    const overlay = document.querySelector('.incremental-search')
    if (!isMobileViewport() || !document.body.classList.contains('wiki-mobile-searching')) {
      clearOverlayPin(overlay)
      return
    }
    const footer = document.querySelector('footer')
    if (!footer) return
    // static.html uses interactive-widget=resizes-content — only position the
    // results overlay; re-pinning the footer dismisses the IME on Android.
    if (overlay) pinOverlayAboveFooter(overlay, footer)
  }

  const onViewportChange = () => {
    if (layoutRaf != null) return
    layoutRaf = requestAnimationFrame(() => {
      layoutRaf = null
      layoutMobileSearch()
    })
  }

  const bindViewport = () => {
    if (viewportBound) return
    viewportBound = true
    window.visualViewport?.addEventListener('resize', onViewportChange)
    window.visualViewport?.addEventListener('scroll', onViewportChange)
    window.addEventListener('resize', onViewportChange)
  }

  const unbindViewport = () => {
    if (!viewportBound) return
    viewportBound = false
    if (layoutRaf != null) {
      cancelAnimationFrame(layoutRaf)
      layoutRaf = null
    }
    window.visualViewport?.removeEventListener('resize', onViewportChange)
    window.visualViewport?.removeEventListener('scroll', onViewportChange)
    window.removeEventListener('resize', onViewportChange)
  }

  // --- Mobile search layout (body class + footer freeze + overlay pin) ---
  const setMobileSearching = on => {
    const want = !!(on && isMobileViewport())
    const isOn = document.body.classList.contains('wiki-mobile-searching')
    // Desktop placeIncrementalSearch calls setMobileSearching(false) every keystroke —
    // no-op when already off so we don't tear down overlay pins / schedule footer work.
    if (want === isOn) {
      if (want) {
        bindViewport()
        layoutMobileSearch()
      }
      return
    }
    if (!want) restoringSearchFocus = false
    document.body.classList.toggle('wiki-mobile-searching', want)
    // Desktop + mobile: collapse flag band so search sits on one-row footer height.
    document.body.classList.toggle('wiki-searching', want)
    setBackgroundScrollBlocked(want)
    if (want) {
      pushSearchHistory()
      // Freeze footer stack/width before neighbors layout — mid-search resize must
      // not re-run syncFooterSearchWidth and shrink the expanded search field.
      try {
        require('./neighbors').freezeFooterStack()
        require('./neighbors').requestLayout()
      } catch (_) {
        /* optional during early boot */
      }
      bindViewport()
      layoutMobileSearch()
    } else {
      consumeSearchHistory()
      unbindViewport()
      clearOverlayPin(document.querySelector('.incremental-search'))
      // Restore pre-search one-row vs stacked after search ends.
      requestAnimationFrame(() => {
        try {
          require('./neighbors').unfreezeFooterStack()
        } catch (_) {
          /* optional during early boot */
        }
      })
    }
  }

  // Desktop: re-pin when the search field moves (window resize or footer
  // stack/unstack). Overlay lives on document.body so it cannot affect footer flex.
  let desktopOverlayResizeBound = false
  let lastDesktopOverlayPin = null
  const unbindDesktopOverlayResize = () => {
    if (!desktopOverlayResizeBound) return
    desktopOverlayResizeBound = false
    lastDesktopOverlayPin = null
    window.removeEventListener('resize', onDesktopOverlayResize)
    document.removeEventListener('wiki:footer-layout', onDesktopOverlayResize)
  }
  const onDesktopOverlayResize = () => {
    if (isMobileViewport()) {
      unbindDesktopOverlayResize()
      return
    }
    const el = document.querySelector('.incremental-search')
    if (!el) {
      unbindDesktopOverlayResize()
      return
    }
    placeIncrementalSearch($(el))
  }
  const bindDesktopOverlayResize = () => {
    if (desktopOverlayResizeBound) return
    desktopOverlayResizeBound = true
    window.addEventListener('resize', onDesktopOverlayResize)
    document.addEventListener('wiki:footer-layout', onDesktopOverlayResize)
  }

  const placeIncrementalSearch = $el => {
    const $searchbox = $('.searchbox')
    if (!$searchbox.length) return
    // Always on <body> — never a footer descendant (that polluted flex math).
    if (!$el.parent().is('body')) $el.appendTo(document.body)
    const mobile = isMobileViewport()

    if (mobile) {
      setMobileSearching(true)
      unbindDesktopOverlayResize()
      layoutMobileSearch()
      return
    }

    // Desktop: fixed to the viewport, anchored above the search field.
    // One-row footer chrome while open (same as mobile) so the panel sits on
    // the standard controls-row height, not above a tall flag stack.
    setMobileSearching(false)
    document.body.classList.add('wiki-searching')
    try {
      require('./neighbors').requestLayout()
    } catch (_) {
      /* optional during early boot */
    }
    document.dispatchEvent(new CustomEvent('wiki:footer-layout'))
    bindDesktopOverlayResize()
    const fieldEl = $searchbox.find('.search-field')[0] || $searchbox[0]
    // Reflow after .wiki-searching collapses any stacked flag band.
    void document.querySelector('footer')?.offsetHeight
    const PREFERRED_W = 450
    const MARGIN = 12
    const vw = window.innerWidth || document.documentElement.clientWidth || PREFERRED_W
    const vh = window.innerHeight || document.documentElement.clientHeight || 0
    const fieldRect = fieldEl.getBoundingClientRect()
    let viewportLeft = fieldRect.left
    if (viewportLeft + PREFERRED_W > vw - MARGIN) {
      viewportLeft = vw - MARGIN - PREFERRED_W
    }
    if (viewportLeft < MARGIN) {
      // Prefer hanging off the right over shrinking the panel.
      viewportLeft = MARGIN
    }
    const left = Math.round(viewportLeft)
    // Fixed: distance from viewport bottom to just above the field.
    const bottom = Math.round(vh - fieldRect.top + 4)
    const pin = { left, bottom, width: PREFERRED_W }
    // DevTools inspect / focus UI can fire resize without the field moving —
    // skip style writes so the panel doesn't jump under the picker.
    // Important: do not clearOverlayPin before this check — wiping then returning
    // leaves CSS position:absolute at the body's static position (bottom-left).
    if (
      lastDesktopOverlayPin &&
      lastDesktopOverlayPin.left === pin.left &&
      lastDesktopOverlayPin.bottom === pin.bottom &&
      lastDesktopOverlayPin.width === pin.width
    ) {
      return
    }
    lastDesktopOverlayPin = pin
    // Overwrite any mobile full-bleed pin (top/left/right/width) in one write.
    $el.css({
      position: 'fixed',
      left: `${left}px`,
      right: 'auto',
      top: 'auto',
      width: `${PREFERRED_W}px`,
      height: 'auto',
      maxWidth: `${PREFERRED_W}px`,
      maxHeight: '300px',
      bottom: `${bottom}px`,
      marginBottom: '0',
      // Above footer (z-index 1000) so the panel isn't covered by flags.
      zIndex: 1001,
      boxSizing: 'border-box',
    })
  }

  const endMobileSearchUi = () => {
    document.body.classList.remove('wiki-searching')
    setMobileSearching(false)
    unbindDesktopOverlayResize()
  }

  // Popstate second-back: drop overlay + search UI, keep input.value.
  hideSearchOverlayKeepQuery = () => {
    $('.incremental-search').remove()
    endMobileSearchUi()
    const input = searchInput()
    if (input && document.activeElement === input) input.blur()
  }

  const incrementalSearch = function (searchQuery) {
    if (searchQuery.length < 2) {
      $('.incremental-search').remove()
      // Opening mobile search freezes/relayouts the footer and can blur the input
      // before this runs (same focus turn). Don't tear down just because :focus
      // already flipped — body.wiki-mobile-searching means we still want the search UI.
      if (
        isMobileViewport() &&
        ($('input.search').is(':focus') ||
          document.body.classList.contains('wiki-mobile-searching'))
      ) {
        setMobileSearching(true)
      } else if (!isMobileViewport() && $('input.search').is(':focus')) {
        // Desktop: keep one-row footer chrome while the field is focused, even
        // before there are enough characters for a results panel.
        document.body.classList.add('wiki-searching')
        unbindDesktopOverlayResize()
      } else {
        endMobileSearchUi()
      }
      return
    }
    if ($('.incremental-search').length === 0) {
      const $overlay = $('<div/>')
        .addClass('incremental-search')
        .attr('tabindex', '-1')
        .on('click', '.internal', function (e) {
          if (e.target.nodeName === 'SPAN') {
            e.target = $(e.target).parent()[0]
          }
          let name = $(e.target).data('pageName')
          // ensure that name is a string (using string interpolation)
          name = `${name}`
          pageHandler.context = $(e.target).attr('title').split(' => ')
          finishClick(e, name)
          // keep focus on clicked result so more results can be opened
          e.target.focus()
          return false
        })
        .on('click', 'img.remote', function (e) {
          // expand to handle click on temporary flag
          if ($(e.target).attr('src').startsWith('data:image/png')) {
            e.preventDefault()
            const site = $(e.target).data('site')
            wiki.site(site).refresh(function () {})
          } else {
            const name = $(e.target).data('slug')
            pageHandler.context = [$(e.target).data('site')]
            return finishClick(e, name)
          }
          return false
        })
        // Keep scroll gestures inside the results panel.
        .on('wheel touchmove', function (e) {
          e.stopPropagation()
        })
      placeIncrementalSearch($overlay)
    } else {
      placeIncrementalSearch($('.incremental-search'))
    }

    const searchResults = neighborhood.search(searchQuery)
    const searchTerms = searchQuery
      .split(' ')
      .map(t => t.toLowerCase())
      .filter(String)
    const searchHighlightRegExp = new RegExp(
      '\\b(' +
        searchQuery
          .split(' ')
          .map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
          .filter(String)
          .join('|') +
        ')',
      'i',
    )
    const highlightText = text =>
      text
        .split(searchHighlightRegExp)
        .map(function (p) {
          if (searchTerms.includes(p.toLowerCase())) {
            return `{{${p}}}`
          } else {
            return p
          }
        })
        .join('')
    const $search = $('.incremental-search').empty()
    if (!searchResults.finds || searchResults.finds.length === 0) {
      $('<div/>').text('No results found').addClass('no-results').appendTo($search)
    }
    let count = 0
    const max_results = 100
    for (const result of searchResults.finds) {
      count++
      if (count === max_results + 1) {
        $('<div/>')
          .text(`${searchResults.finds.length - max_results} results omitted`)
          .addClass('omitted-results')
          .appendTo($search)
      }
      if (count > max_results) {
        continue
      }
      const $item = $('<div/>').appendTo($search)
      const item = {
        id: random.itemId(),
        type: 'reference',
        site: result.site,
        slug: result.page.slug,
        title: highlightText(result.page.title),
        text: highlightText(result.page.synopsis),
      }
      emit($item, item)
      $item.html(
        $item
          .html()
          .split(new RegExp('({{.*?}})', 'i'))
          .map(p => {
            if (p.indexOf('{{') === 0) {
              return `<span class='search-term'>${p.substring(2, p.length - 2)}</span>`
            } else {
              return p
            }
          })
          .join(''),
      )
    }
    if (isMobileViewport()) layoutMobileSearch()
  }

  const performSearch = function (searchQuery) {
    const searchResults = neighborhood.search(searchQuery)
    if (searchResults.finds && searchResults.finds.length === 1) {
      $('.incremental-search').find('.internal').trigger('click')
      $('.incremental-search').remove()
      endMobileSearchUi()
      return
    }
    $('.incremental-search').remove()
    endMobileSearchUi()
    const { tally } = searchResults
    const resultPage = {}
    resultPage.title = `Search for '${searchQuery}'`
    resultPage.story = []
    resultPage.story.push({
      type: 'paragraph',
      id: random.itemId(),
      text: `\
String '${searchQuery}' found on ${tally.finds || 'none'} of ${tally.pages || 'no'} pages from ${tally.sites || 'no'} sites.
Text matched on ${tally.title || 'no'} titles, ${tally.text || 'no'} paragraphs, and ${tally.slug || 'no'} slugs.
Elapsed time ${tally.msec} milliseconds.\
`,
    })
    for (var result of searchResults.finds) {
      resultPage.story.push({
        id: random.itemId(),
        type: 'reference',
        site: result.site,
        slug: result.page.slug,
        title: result.page.title,
        text: result.page.synopsis || '',
      })
    }

    resultPage.journal = [
      {
        type: 'create',
        item: {
          title: resultPage.title,
          story: deepCopy(resultPage.story),
        },
        date: Date.now(),
      },
    ]
    const pageObject = newPage(resultPage)
    link.showResult(pageObject)
  }

  return {
    incrementalSearch,
    performSearch,
    endMobileSearchUi,
    setMobileSearching,
    isRestoringSearchFocus,
    clearRestoringSearchFocus,
  }
}
module.exports = createSearch
